import { NavLink } from "react-router-dom";
import Logo from "@assets/images/ampli-logo.svg";

import {
  useDashboardUIActions,
  useDashboardUIContext,
} from "@contexts/dashboardUI";
import { useProjectContext } from "@contexts/projects";
import { useBimActions, useBimContext } from "@contexts/bim";
import { useAuthActions, useAuthContext } from "@contexts/auth";
import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCostsActions } from "@contexts/costs";
import { checkRoles } from "@utils/permissions";
import Select from 'react-select';
import {
  dashboardPaths,
  resolveDashboardPathLabel,
  resolveDashboardPathPermission,
} from "@outlets/configs/options";
import { useDirectorContext } from "@contexts/director";

import {
  COMPLEMENTARY_LABEL,
  COMPLEMENTARY_PATH,
  PRIMARY_SCREENS,
  isComplementaryPath,
  visibleComplementarySections,
} from "@outlets/director/complementaryPages";

// const dashboardPaths = [{
//   path: "valor-agregado",
//   label: "Valor agregado",
// }, {
//   path: "projecao-financeira",
//   label: "Projeção financeira"
// }, {
//   path: "curva",
//   label: "Curva ABC"
// }, {
//   path: "adiantamentos",
//   label: "Adiantamentos"
// }, {
//   path: "valores",
//   label: "Valores não lançados"
// }, {
//   path: "titulos",
//   label: "Títulos"
// }, {
//   path: "licoes",
//   label: "Lições aprendidas"
// }, {
//   path: "pedidoscontratos",
//   label: "Pedidos e Contratos"
// }
// ]

const Sidebar = () => {
  const { pathname } = useLocation();
  const { activeProject, projects } = useProjectContext();
  const { showSidebar } = useDashboardUIContext();
  const { closeSidebar } = useDashboardUIActions();
  const { logout } = useAuthActions()
  const { getProjects, selectProject } = useBimActions()
  const { currentProject } = useBimContext()
  const { getBuildings, selectBuilding } = useCostsActions()
  const { user } = useAuthContext();
  const director = useDirectorContext();
  const [visibles, setVisibles] = useState({
    backoffice: pathname.includes('backoffice'),
    bim: pathname.includes('bim'),
    costs: pathname.includes('costs'),
    director: pathname.includes('director'),
    titles: pathname.startsWith('/dashboard/titulos'),
  })
  const [bimProjects, setBimProjects] = useState([])
  const [costsBuildings, setCostsBuildings] = useState([])
  const closeButtonRef = useRef(null)
  const closeSidebarActionRef = useRef(closeSidebar)
  closeSidebarActionRef.current = closeSidebar

  useEffect(() => {
    if (!showSidebar) return undefined;

    if (window.innerWidth < 1024) closeButtonRef.current?.focus();

    const handleEscape = (event) => {
      if (event.key !== "Escape" || window.innerWidth >= 1024) return;
      event.preventDefault();
      closeSidebarActionRef.current();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showSidebar]);

  // SPEC 5.8: no celular a sidebar é uma gaveta; escolher um destino fecha a gaveta.
  const handleNavigationClick = (event) => {
    if (window.innerWidth >= 1024) return
    if (event.target?.closest?.('a')) closeSidebar()
  }

  const handleOpenClose = (e) => {
    const name = e.target.getAttribute("name")
    setVisibles((prev) => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  const getBimProjects = async () => {
    const res = await getProjects(user.bimProjectsIds);
    setBimProjects(res.res);
  }

  const getCostsBuildings = async () => {
    const res = await getBuildings(user.costsBuildingsIds ?? ['000000000000000000001000'])
    setCostsBuildings(res.res)
  }

  useEffect(() => {
    if (visibles.bim) {
      getBimProjects()
    }
    if (visibles.costs) {
      getCostsBuildings()
    }
  }, [visibles, user])

  const handleBimProjectChange = (e) => {
    const project = bimProjects.filter(i => i.name === e.value)[0]
    selectProject(project)
  }

  const handleCostsBuildingChange = (e) => {
    const building = costsBuildings[e.index]
    selectBuilding(building)
  }

  const navigationProject = activeProject ?? projects?.[0]
  const navigationProjectId = navigationProject?._id
  const isTrial = navigationProject?.building_level === 'trial'
  const directorAccess = director.selectedCompanyId
    ? director.accessByCompany?.[director.selectedCompanyId]
    : null
  const directorCapabilities = directorAccess?.capabilities ?? []
  const canViewCashflow = directorCapabilities.includes('director.cashflow.read')
  const canViewCostPlan = directorCapabilities.includes('director.cost_plan.read')
  const canViewReceivables = directorCapabilities.includes('receivables.summary.read')
  const canViewLiquidity = directorCapabilities.includes('director.liquidity.read')
  const canViewDelinquency = directorCapabilities.includes('receivables.details.read')
  const canViewDirector = director.bootstrapStatus === 'ready'
    && directorCapabilities.includes('director.view')
    && (canViewCashflow || canViewReceivables || canViewCostPlan || canViewLiquidity || canViewDelinquency)
  // Change 12: as 4 telas principais e uma entrada para Informações complementares, sem
  // acordeão aninhado. Cada item segue a capability da própria rota.
  const directorScreens = PRIMARY_SCREENS.filter((screen) => directorCapabilities.includes(screen.capability))
  const canViewComplementary = visibleComplementarySections(directorCapabilities).length > 0
  const complementaryCurrent = isComplementaryPath(pathname)
  const directorCompanyQuery = director.selectedCompanyId
    ? `?company_id=${encodeURIComponent(director.selectedCompanyId)}`
    : ''

  const CheckPermissions = ({ path, label, permission = label }) => {
    const pageViews = user?.permissions?.pageViews
    if (navigationProjectId && pageViews && pageViews.includes(permission)) {
      return <NavLink to={`/dashboard/${path}/${navigationProjectId}`}>{label}</NavLink>
    }
  }

  // "Títulos" abre um submenu: os títulos pagos e a pagar da obra e a lista de títulos anulados
  // na Ampli da construtora. Os dois usam a mesma permissão de página ("Títulos"). É uma função
  // que devolve JSX (não um componente) para o botão não remontar a cada clique e manter o foco.
  const canViewTitles = Boolean(navigationProjectId && user?.permissions?.pageViews?.includes('Títulos'))
  const renderTitlesGroup = () => (
    <Fragment key="titulos">
      <button
        type="button"
        className={`sidebar-nav-group ${pathname.startsWith('/dashboard/titulos') ? 'current' : ''}`}
        aria-expanded={visibles.titles}
        aria-controls="sidebar-titles-submenu"
        onClick={() => setVisibles((prev) => ({ ...prev, titles: !prev.titles }))}
      >
        Títulos
        <i className={`icon icon-width-16 icon-angle-down sidebar-nav-group__chevron ${visibles.titles ? 'open' : ''}`} aria-hidden={true}></i>
      </button>
      {visibles.titles && (
        <div id="sidebar-titles-submenu" className="submenu submenu--nested">
          <NavLink to={`/dashboard/titulos/${navigationProjectId}`}>Pago e a pagar</NavLink>
          <NavLink to={`/dashboard/titulos-anulados/${navigationProjectId}`}>Títulos anulados</NavLink>
        </div>
      )}
    </Fragment>
  )


  return (
    showSidebar && (
      <Fragment>
        <button
          type="button"
          className="dashboard-sidebar-backdrop"
          aria-label="Fechar menu pelo fundo"
          aria-controls="dashboard-sidebar"
          onClick={closeSidebar}
        />
        <aside id="dashboard-sidebar" className="dashboard-sidebar active">
        <div>
          <div className="flex gap-16 align-center justify-content-space-between padding-horizontal-24 padding-horizontal-32-laptop-l">
            <figure id="dashboard-logo">
              <img src={Logo} alt="Ampli" />
            </figure>
            {showSidebar && (<button
              ref={closeButtonRef}
              type="button"
              className="toggle-menu-button"
              aria-label="Fechar menu de navegação"
              aria-controls="dashboard-sidebar"
              aria-expanded="true"
              onClick={closeSidebar}
            >
              <i
                className="icon icon-width-24 icon-angle-double-left background-accent"
                aria-hidden={true}
              ></i>
            </button>
            )}
          </div>
          <nav id="dashboard-nav" className="grid margin-top-48" onClick={handleNavigationClick}>
            {user && (user?.roles.includes('ampli') || user?.roles.includes('admin')) && <>
              {(!isTrial && navigationProjectId) && (
                <NavLink to={`/dashboard/${navigationProjectId}`}>
                  Dashboard
                </NavLink>
              )}

              {dashboardPaths.map((item) => (item.path === 'titulos' ? (canViewTitles && renderTitlesGroup()) : (
                <CheckPermissions
                  key={item.path}
                  path={item.path}
                  label={resolveDashboardPathLabel(item, activeProject?.cashFlowPlanning)}
                  permission={resolveDashboardPathPermission(item)}
                />
              )))}
            </>}
            {user && user?.roles.includes('admin') &&
              <>
                <div className="sidebar-section-header">
                  <button
                    className={`accordion-toggle ${visibles.backoffice ? "active" : ""}`}
                  // onClick={handleToggle}
                  />
                  <span
                    onClick={handleOpenClose}
                    className="menu-header"
                    name='backoffice'
                  >
                    Backoffice
                  </span>
                </div>
                {visibles.backoffice && (
                  <div className="submenu">
                    <NavLink to={`/dashboard/backoffice/users`}>
                      Usuários
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/companies-buildings`}>
                      Construtoras e Obras
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/indexers`}>
                      Indexadores
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/cartesian-forms`}>
                      Formulários
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/supplier-explorer`}>
                      Explorador de fornecedores
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/audit`}>
                      Auditoria
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/integrations`}>
                      Integrações
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/sienge-permissions`}>
                      Permissões Sienge
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/product-access`}>
                      Gestão de Produtos
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/financial-governance`}>
                      Governança Financeira
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/title-annulments`}>
                      Títulos anulados
                    </NavLink>
                    <NavLink to={`/dashboard/backoffice/activity`}>
                      Uso da plataforma
                    </NavLink>
                  </div>
                )}
              </>
            }
            {canViewDirector && <>
              <div className="sidebar-section-header">
                <button className={`accordion-toggle ${visibles.director ? "active" : ""}`} />
                <span onClick={handleOpenClose} className="menu-header" name="director">Diretoria</span>
              </div>
              {visibles.director && <div className="submenu">
                {directorScreens.map((screen) => <NavLink key={screen.key} to={`${screen.path}${directorCompanyQuery}`}>
                  {screen.label}
                </NavLink>)}
                {canViewComplementary && <NavLink
                  to={`${COMPLEMENTARY_PATH}${directorCompanyQuery}`}
                  className={({ isActive }) => (isActive || complementaryCurrent ? 'active' : undefined)}
                >
                  {COMPLEMENTARY_LABEL}
                </NavLink>}
              </div>}
            </>}
            {user && user?.roles.includes('costs') && <>
              <div className="sidebar-section-header">
                <button
                  className={`accordion-toggle ${visibles.costs ? "active" : ""}`}
                // onClick={handleToggle}
                />
                <span
                  onClick={handleOpenClose}
                  className="menu-header"
                  name="costs"
                >
                  Custos
                </span>
              </div>
              {visibles.costs && (
                <div className="submenu">
                  <Select
                    isClearable={false}
                    name="bim-projects"
                    options={costsBuildings.map((i, idx) => ({ value: i.name, label: i.name, index: idx }))}
                    onChange={(e) => handleCostsBuildingChange(e)}
                    className="basic-select bim-project-select"
                    classNamePrefix="select"
                    placeholder='Selecione um projeto'
                  />
                  <NavLink to={`/dashboard/costs/obras`}>
                    Obras
                  </NavLink>
                  <NavLink to={`/dashboard/costs/parametros`}>
                    Parâmetros
                  </NavLink>
                  <NavLink to={`/dashboard/costs/unidades`}>
                    Unidades
                  </NavLink>
                  <NavLink to={`/dashboard/costs/precos`}>
                    Valor Unitários
                  </NavLink>
                  <NavLink to={`/dashboard/costs/itens`}>
                    Itens
                  </NavLink>
                  <NavLink to={`/dashboard/costs/cub`}>
                    CUB
                  </NavLink>
                  <NavLink to={`/dashboard/costs/resumos`}>
                    Orçamentos Resumidos
                  </NavLink>
                  <NavLink to={`/dashboard/costs/comparacao`}>
                    Comparação de Orçamentos
                  </NavLink>
                  <NavLink to={`/dashboard/costs/fornecedores`}>
                    Fornecedores
                  </NavLink>
                </div>
              )}
            </>}

            {user && (user?.roles.includes('bim') || user?.roles.includes('bimAdmin')) && <>
              <div className="sidebar-section-header">
                <button
                  className={`accordion-toggle ${visibles.bim ? "active" : ""}`}
                // onClick={handleToggle}
                />
                <span
                  onClick={handleOpenClose}
                  className="menu-header"
                  name='bim'
                >
                  BIM
                </span>
              </div>
              {visibles.bim && (
                <div className="submenu">
                  <Select
                    style={{ width: '100%' }}
                    isClearable={false}
                    name="bim-projects"
                    options={bimProjects.map(i => ({ value: i.name, label: i.name }))}
                    onChange={(e) => handleBimProjectChange(e)}
                    className="basic-select bim-project-select"
                    classNamePrefix="select"
                    placeholder='Selecione um projeto'
                  />
                  <NavLink to={`/dashboard/bim/cadastro`}>
                    Cadastro de Projetos
                  </NavLink>
                  <NavLink to={`/dashboard/bim/entregaveis`}>
                    Controle de Entregáveis
                  </NavLink>
                  <NavLink to={`/dashboard/bim/checklist`}>
                    Checklist de Verificação
                  </NavLink>
                  <NavLink to={`/dashboard/bim/cronograma`}>
                    Cronograma
                  </NavLink>
                  <NavLink to={`/dashboard/bim/opcoes`}>
                    Opções
                  </NavLink>
                  {user?.roles.includes('bimAdmin') &&
                    <>
                      <NavLink to={`/dashboard/bim/classificacao`}>
                        Classificação de Apontamento
                      </NavLink>
                      <NavLink to={`/dashboard/bim/licoes`}>
                        Lições Aprendidas
                      </NavLink>
                    </>
                  }
                </div>
              )}</>}
          </nav>
          <div className="sidebar-bottom">
            {navigationProjectId && (
              <div className="clickable">
                <i
                  className="icon icon-width-24 icon-info-circle background-gray"
                  aria-hidden={true}
                ></i>
                <NavLink to={`/dashboard/sobre/${navigationProjectId}`}>
                  Sobre
                </NavLink>
              </div>
            )}
            <div className="clickable">
              <i
                className="icon icon-width-24 icon-cog background-gray"
                aria-hidden={true}
              ></i>
              <NavLink to={`/dashboard/settings`}>
                Configurações
              </NavLink>
            </div>
            <button onClick={logout} className="logout-button">
              <i
                className="icon icon-width-24 icon-sign-out background-gray"
                aria-hidden={true}
              ></i>
              <span>Sair</span>
            </button>
          </div>
        </div>
        </aside >
      </Fragment>
    )
  );
};

export default Sidebar;
